import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { apiV2 } from "api/firebase";

import {
  IAssignSupplierToShowReq,
  ICheckIsSupplierRelatedToShowReq,
  ICheckIsSupplierRelatedToShowRes,
  ICreateSupplierWithAssignShowReq,
  IDeleteSupplierFromShowReq,
  IGetByCompanySupplierQueryReq,
  IGetOneByEmailSupplierQueryReq,
  IGetOneByUserIdSupplierQueryReq,
  IGetOneSupplierQueryReq,
  IGetSupplierAssignableToShowQueryReq,
  IGetSupplierByCompanySupplierRelatedToShowQueryReq,
  IUpdateSupplierReq,
} from "@evennt-tech/api-v2-external";

import {
  INotificationBaseMessage,
  IPayloadUpdateSupplier,
  ISupplier,
  NotificationType,
} from "@evennt-tech/api-v2-core";
import { getErrorMessage } from "tools/error";
import { IGetAllByIdsSupplierReq } from "@evennt-tech/api-v2-external/dist/gateways/supplier/interfaces";
import { registerStore } from "store/utils/reset-store.utils";
import { v4 as uuid } from "uuid";

type State = {
  items: ISupplier[];
  itemsByIds: ISupplier[];
  itemsRelatedToShow: ISupplier[];
  itemsAssignableToShow: ISupplier[];
  selected: ISupplier | null;
  isLoading: boolean;
  error: string | null;
  relatedSupplierToShowId: string | null;
  isAdmin: boolean;
};

type Action = {
  fetchRelatedToShow: (
    params: IGetSupplierByCompanySupplierRelatedToShowQueryReq
  ) => Promise<void>;

  checkIsAnyItemCanRefund: () => boolean;

  autoCatchRelatedToShow: (
    params: IGetSupplierByCompanySupplierRelatedToShowQueryReq
  ) => Promise<void>;

  autoCatchAssignableToShow: (
    params: IGetSupplierAssignableToShowQueryReq
  ) => Promise<void>;

  autoCatchByCompany: (supplierCompanyId: string) => () => void;
  autoCatchOne: () => () => void;

  fetchByCompany: (
    params: IGetByCompanySupplierQueryReq,
    force?: boolean
  ) => Promise<void>;

  fetchByAllByIds: (params: IGetAllByIdsSupplierReq) => Promise<void>;

  fetchSuppliersAssignableToShow: (
    params: IGetSupplierAssignableToShowQueryReq
  ) => Promise<void>;

  fetchOne: (params: IGetOneSupplierQueryReq) => Promise<void>;

  fetchOneByUserId: (params: IGetOneByUserIdSupplierQueryReq) => Promise<void>;

  checkIsSupplierRelatedToShow: (
    params: ICheckIsSupplierRelatedToShowReq
  ) => Promise<ICheckIsSupplierRelatedToShowRes | null>;

  edit: (body: IUpdateSupplierReq) => Promise<void>;

  assignSupplierToShow: (body: IAssignSupplierToShowReq) => Promise<void>;

  deleteSupplierFromShow: (body: IDeleteSupplierFromShowReq) => Promise<void>;

  createSupplierWithShowAssign: (
    body: ICreateSupplierWithAssignShowReq
  ) => Promise<void>;

  fetchOneByEmail: (params: IGetOneByEmailSupplierQueryReq) => Promise<void>;

  clearSelected: () => void;

  setSelected: (supplier: ISupplier | null) => void;

  getSelected: () => ISupplier | null;

  setError: (message: string | null) => void;
};

export const useSuppliers = create<State & Action>()(
  devtools((set, get) => ({
    items: [],
    itemsByIds: [],
    itemsRelatedToShow: [],
    itemsAssignableToShow: [],
    selected: null,
    isLoading: false,
    error: null,
    relatedSupplierToShowId: null,
    isAdmin: false,

    setError: (message) => set({ error: message }),

    setSelected: (supplier) => set({ selected: supplier }),
    getSelected: () => get().selected,

    checkIsAnyItemCanRefund: () =>
      !!get().items.find((item) => item.canApproveRefunds),

    fetchSuppliersAssignableToShow: async (params) => {
      try {
        const res = await apiV2.supplier.getSuppliersAssignableToShow(params);
        set({ itemsAssignableToShow: res ?? [] });
      } catch (e) {
        console.error("Failed to fetch all suppliers:", e);
      }
    },

    fetchByCompany: async (params, force?: boolean) => {
      const isLoadedAnyItem = !!get().items?.length;
      if (isLoadedAnyItem && !force) {
        return;
      }
      set({ isLoading: true });
      try {
        const res = await apiV2.supplier.getByCompany(params);
        set({ items: res ?? [] });
      } catch (e) {
        console.error("Failed to fetch all suppliers:", e);
      } finally {
        set({ isLoading: false });
      }
    },

    fetchByAllByIds: async (params) => {
      set({ isLoading: true });
      try {
        const res = await apiV2.supplier.getAllByIds(params);
        set({ itemsByIds: res ?? [] });
      } catch (e) {
        console.error("Failed to fetch all suppliers:", e);
      } finally {
        set({ isLoading: false });
      }
    },

    clearSelected: () => set({ selected: null }),

    fetchOne: async (params) => {
      try {
        const data = await apiV2.supplier.getOne(params);
        set({ selected: data });
      } catch (e) {
        console.error("Failed to fetch supplier:", e);
        set({ selected: null });
      }
    },

    autoCatchAssignableToShow: async (params) => {
      const { fetchSuppliersAssignableToShow } = get();
      const keys = [
        NotificationType.CREATED_SUPPLIER,
        NotificationType.UPDATED_SUPPLIER,
        NotificationType.DELETED_SUPPLIER,
        NotificationType.ASSIGN_SUPPLIER_TO_SHOW,
        NotificationType.DELETE_SUPPLIER_FROM_SHOW,
      ];

      for (const key of keys) {
        apiV2.notification.subscribe({
          key,
          callback: () => fetchSuppliersAssignableToShow(params),
        });
      }
    },

    autoCatchRelatedToShow: async (params) => {
      const { fetchRelatedToShow } = get();
      const keys = [
        NotificationType.CREATED_SUPPLIER,
        NotificationType.UPDATED_SUPPLIER,
        NotificationType.DELETED_SUPPLIER,
        NotificationType.CREATED_SUPPLIER_COMPANY,
        NotificationType.UPDATED_SUPPLIER_COMPANY,
        NotificationType.DELETED_SUPPLIER_COMPANY,
        NotificationType.ASSIGN_SUPPLIER_TO_SHOW,
        NotificationType.DELETE_SUPPLIER_FROM_SHOW,
      ];

      for (const key of keys) {
        apiV2.notification.subscribe({
          key,
          callback: () => fetchRelatedToShow(params),
        });
      }
    },

    autoCatchByCompany: (supplierCompanyId) => {
      const { fetchByCompany } = get();

      const listenerId = uuid();
      apiV2.notification.subscribe<
        INotificationBaseMessage<IPayloadUpdateSupplier>
      >({
        key: NotificationType.UPDATED_SUPPLIER,
        listenerId,
        callback: async ({ payload }) => {
          if (!payload) return;

          const { items } = get();
          const currentSupplierCompanyId =
            items[0]?.supplierCompanyToSuppliers?.[0]?.supplierCompanyId;
          if (!currentSupplierCompanyId) return;

          if (payload.supplierCompanyIds.includes(currentSupplierCompanyId)) {
            await fetchByCompany({ supplierCompanyId }, true);
          }
        },
      });

      return () =>
        apiV2.notification.unsubscribe({
          key: NotificationType.UPDATED_SUPPLIER,
          listenerId,
        });
    },

    autoCatchOne: () => {
      const { fetchOne } = get();
      const listenerId = uuid();

      apiV2.notification.subscribe<
        INotificationBaseMessage<IPayloadUpdateSupplier>
      >({
        key: NotificationType.UPDATED_SUPPLIER,
        listenerId,
        callback: async ({ payload }) => {
          if (!payload) return;

          const { selected } = get();
          const shouldUpdate =
            selected?.id && selected.id === payload.supplierId;
          if (!shouldUpdate) {
            return;
          }
          await fetchOne({ supplierId: payload.supplierId });
        },
      });

      return () =>
        apiV2.notification.unsubscribe({
          key: NotificationType.UPDATED_SUPPLIER,
          listenerId,
        });
    },

    fetchRelatedToShow: async (params) => {
      try {
        const { showId, supplierCompanyId } = params;
        const suppliers = await apiV2.supplier.getSuppliersByCompanySupplierRelatedToShow(
          {
            showId,
            supplierCompanyId,
          }
        );
        set({ itemsRelatedToShow: suppliers, error: null });
      } catch (e) {
        console.error("Failed to fetch suppliers:", e);
      }
    },

    fetchOneByUserId: async (params) => {
      try {
        const data = await apiV2.supplier.getOneByUserId(params);
        set({ selected: data, error: null });
      } catch (e) {
        console.error("Failed to fetch supplier:", e);
        set({ selected: null });
      }
    },

    checkIsSupplierRelatedToShow: async (params) => {
      try {
        const res = await apiV2.supplier.checkIsSupplierRelatedToShow(params);

        if (res) {
          set(() => ({
            relatedSupplierToShowId: res.supplierId,
            isAdmin: res.isAdmin,
            error: null,
          }));
        }

        return res;
      } catch (e) {
        console.error("Failed check is supplier related show:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    edit: async (body) => {
      try {
        const res = await apiV2.supplier.edit(body);
        set((state) => ({
          items: state.items.map((item) => (item.id === res.id ? res : item)),
          selected: res,
        }));
      } catch (e) {
        console.error("Failed to edit supplier:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    assignSupplierToShow: async (body) => {
      try {
        const res = await apiV2.supplier.assignSupplierToShow(body);
        set((state) => ({
          itemsRelatedToShow: [...state.itemsRelatedToShow, res],
          itemsAssignableToShow: state.itemsAssignableToShow.filter(
            (item) => item.id !== body.supplierId
          ),
          error: null,
        }));
      } catch (e) {
        console.error("Failed to assign supplier:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    deleteSupplierFromShow: async (body) => {
      try {
        const res = await apiV2.supplier.deleteSupplierFromShow(body);
        set((state) => ({
          itemsRelatedToShow: state.itemsRelatedToShow.filter(
            (item) => item.id !== body.supplierId
          ),
          error: null,
        }));
      } catch (e) {
        console.error("Failed to delete supplier:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    fetchOneByEmail: async (params) => {
      try {
        const data = await apiV2.supplier.getOneByEmail(params);
        set({ selected: data, error: null });
      } catch (e) {
        console.error("Failed to fetch supplier:", e);
        set({ selected: null });
      }
    },

    createSupplierWithShowAssign: async (body) => {
      try {
        const res = await apiV2.supplier.createSupplierWithShowAssign(body);
        set((state) => ({
          itemsRelatedToShow: [...state.itemsRelatedToShow, res],
          itemsAssignableToShow: state.itemsAssignableToShow.filter(
            (item) => item.id !== res.id
          ),
          error: null,
        }));
      } catch (e) {
        console.error("Failed to create supplier:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },
  }))
);

registerStore(useSuppliers);
